import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface TemplateFilters {
  channel: string;
  audienceType: string;
  status: string;
}

interface TemplateFilterProps {
  channel: string;
  audienceType: string;
  status: string;
  channelList: Array<{ label: string; value: string }>;
  audienceTypeList: Array<{ label: string; value: string }>;
  statusList: Array<{ label: string; value: string }>;
  onApplyFilters: (filters: TemplateFilters) => void;
  onResetFilters: () => void;
}

export const TemplateFiltersPanel: React.FC<TemplateFilterProps> = ({
  channel,
  audienceType,
  status,
  channelList,
  audienceTypeList,
  statusList,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform lists to options format
  const channelOptions = useMemo(
    () =>
      channelList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [channelList]
  );

  const audienceTypeOptions = useMemo(
    () =>
      audienceTypeList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [audienceTypeList]
  );

  const statusOptions = useMemo(
    () =>
      statusList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [statusList]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "channel",
        label: "Channel",
        type: "select",
        placeholder: "Select Channel",
        options: channelOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "audienceType",
        label: "Audience Type",
        type: "select",
        placeholder: "Select Audience Type",
        options: audienceTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "Select Status",
        options: statusOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [channelOptions, audienceTypeOptions, statusOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      channel: decodeURIComponent(channel) || "",
      audienceType: decodeURIComponent(audienceType) || "",
      status: decodeURIComponent(status) || "",
    });
  }, [channel, audienceType, status, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      channel: formValues.channel || "",
      audienceType: formValues.audienceType || "",
      status: formValues.status || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      channel: "",
      audienceType: "",
      status: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={3}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};